import type { ApiResponse, EventListQuery, SafeEvent } from "@playarena/shared";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import { getEnv } from "@/utils/helpers";

export const eventApi = createApi({
  reducerPath: "eventApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${getEnv()?.VITE_API_URL}/${getEnv()?.VITE_APP_VERSION}/event`,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as any).auth.token;
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ["Event"],
  endpoints: builder => ({
    getEvents: builder.query<ApiResponse<SafeEvent[]>, EventListQuery>({
      query: params => ({
        url: "/list",
        params,
      }),
      onQueryStarted: async (_params, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.success && data.data) {
            // Update events list in slice
            dispatch({ type: "event/setEvents", payload: data.data });
            // Update pagination if available
            if (data.pagination) {
              dispatch({ type: "event/setPagination", payload: data.pagination });
            }
          }
        } catch (error) {
          console.error("eventApi.getEvents error:", error);
        }
      },
      providesTags: ["Event"],
    }),

    getMyEvents: builder.query<ApiResponse<SafeEvent[]>, EventListQuery>({
      query: params => ({
        url: "/list/my",
        params,
      }),
      onQueryStarted: async (_params, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.success && data.data) {
            // Update events list in slice
            dispatch({ type: "event/setEvents", payload: data.data });
            // Update pagination if available
            if (data.pagination) {
              dispatch({ type: "event/setPagination", payload: data.pagination });
            }
          }
        } catch (error) {
          console.error("eventApi.getMyEvents error:", error);
        }
      },
      providesTags: ["Event"],
    }),

    getEventById: builder.query<ApiResponse<SafeEvent>, string>({
      query: id => `/${id}`,
      onQueryStarted: async (_params, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.success && data.data) {
            // Set selected event in slice
            dispatch({ type: "event/setSelectedEvent", payload: data.data });
          }
        } catch (error) {
          console.error("eventApi.getEventById error:", error);
        }
      },
      providesTags: (result, error, id) => [{ type: "Event", id }],
    }),

    createEvent: builder.mutation<
      ApiResponse<SafeEvent>,
      Omit<SafeEvent, "id" | "createdAt" | "updatedAt">
    >({
      query: data => ({
        url: "/create",
        method: "POST",
        body: data,
      }),
      onQueryStarted: async (_params, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.success && data.data) {
            // Add new event to the events list
            dispatch({ type: "event/addEvent", payload: data.data });
          }
        } catch (error) {
          console.error("eventApi.createEvent error:", error);
        }
      },
      invalidatesTags: ["Event"],
    }),

    updateEvent: builder.mutation<
      ApiResponse<SafeEvent>,
      Partial<Omit<SafeEvent, "id">> & { id: string }
    >({
      query: data => ({
        url: `/${data.id}`,
        method: "PUT",
        body: data,
      }),
      onQueryStarted: async (_params, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.success && data.data) {
            // Update event in events list
            dispatch({ type: "event/updateEventInList", payload: data.data });
            // Update selected event if it's the one being updated
            dispatch({ type: "event/setSelectedEvent", payload: data.data });
          }
        } catch (error) {
          console.error("eventApi.updateEvent error:", error);
        }
      },
      invalidatesTags: (result, error, data) => [{ type: "Event", id: data.id }, "Event"],
    }),

    deleteEvent: builder.mutation<ApiResponse<{ message: string }>, string>({
      query: id => ({
        url: `/${id}`,
        method: "DELETE",
      }),
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.success) {
            // Remove event from events list
            dispatch({ type: "event/removeEventFromList", payload: id });
            // Clear selected event if it's the one being deleted
            dispatch({ type: "event/setSelectedEvent", payload: null });
          }
        } catch (error) {
          console.error("eventApi.deleteEvent error:", error);
        }
      },
      invalidatesTags: ["Event"],
    }),

    getEventsByGround: builder.query<ApiResponse<SafeEvent[]>, string>({
      query: groundId => `/ground/${groundId}`,
      providesTags: ["Event"],
    }),

    getEventSeatAvailability: builder.query<
      ApiResponse<{ seats: any[]; availability: any }>,
      string
    >({
      query: eventId => `/${eventId}/seats`,
      providesTags: (result, error, eventId) => [{ type: "Event", id: eventId }],
    }),

    getFeaturedEvents: builder.query<ApiResponse<SafeEvent[]>, void>({
      query: () => "/featured",
      providesTags: ["Event"],
    }),

    getUpcomingEvents: builder.query<ApiResponse<SafeEvent[]>, { limit?: number }>({
      query: params => ({
        url: "/upcoming",
        params,
      }),
      providesTags: ["Event"],
    }),

    searchEvents: builder.query<ApiResponse<SafeEvent[]>, EventListQuery>({
      query: params => ({
        url: "/search",
        params,
      }),
      providesTags: ["Event"],
    }),

    publishEvent: builder.mutation<ApiResponse<SafeEvent>, string>({
      query: id => ({
        url: `/${id}/publish`,
        method: "PATCH",
      }),
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.success && data.data) {
            // Update event in events list
            dispatch({ type: "event/updateEventInList", payload: data.data });
            // Update selected event if it's the one being published
            dispatch({ type: "event/setSelectedEvent", payload: data.data });
          }
        } catch (error) {
          console.error("eventApi.publishEvent error:", error);
        }
      },
      invalidatesTags: (result, error, id) => [{ type: "Event", id }, "Event"],
    }),

    unpublishEvent: builder.mutation<ApiResponse<SafeEvent>, string>({
      query: id => ({
        url: `/${id}/unpublish`,
        method: "PATCH",
      }),
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.success && data.data) {
            // Update event in events list
            dispatch({ type: "event/updateEventInList", payload: data.data });
            // Update selected event if it's the one being unpublished
            dispatch({ type: "event/setSelectedEvent", payload: data.data });
          }
        } catch (error) {
          console.error("eventApi.unpublishEvent error:", error);
        }
      },
      invalidatesTags: (result, error, id) => [{ type: "Event", id }, "Event"],
    }),
  }),
});

export const {
  useGetEventsQuery,
  useGetMyEventsQuery,
  useGetEventByIdQuery,
  useCreateEventMutation,
  useUpdateEventMutation,
  useDeleteEventMutation,
  useGetEventsByGroundQuery,
  useGetEventSeatAvailabilityQuery,
  useGetFeaturedEventsQuery,
  useGetUpcomingEventsQuery,
  useSearchEventsQuery,
  usePublishEventMutation,
  useUnpublishEventMutation,
} = eventApi;
